import { Stack, Table } from "@mantine/core";

import { isEmpty, omit } from "lodash";

import {
  calculateWeightChanges,
  FlowsheetWeightEntry,
} from "@/encounters/utils/prenatal-flowsheet";
import { PrenatalFlowsheetFormType } from "@/encounters/zod-schemas/encounter-answers";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { findObservationByCode } from "@/fhir/observation";
import { convertWeight, getWeightInGrams, isWeightUnit, WeightUnitChoices } from "@/fhir/weight";

import type { PrenatalFlowsheetObservationRow } from "./hooks";
import classes from "./PrenatalFlowsheetTable.module.css";
import {
  PrenatalFlowsheetFormRows,
  PrenatalFlowsheetObservationsRows,
} from "./PrenatalFlowsheetTableRows";

export interface PrenatalFlowsheetTableProps {
  // See usePrenatalFlowsheet in hooks.tsx
  gestationalAge: { weeks: number; days: number } | undefined;
  /** EDD from the linked pregnancy record; used to calculate GA for historical rows. */
  eddDate?: string;
  observations?: PrenatalFlowsheetObservationRow[];
  formValues?: PrenatalFlowsheetFormType;
  isLoading?: boolean;
}

/** Identifies the in-progress encounter's row, which has no saved Observation to key on. */
export const FORM_ROW_KEY = "prenatal-flowsheet-form-row";

const toFormWeightEntry = (
  formValues: PrenatalFlowsheetFormType | undefined,
): FlowsheetWeightEntry => {
  const weight = formValues?.encounterFlowSheetWeight;
  const unit = formValues?.encounterFlowSheetWeightUnit;

  return {
    rowKey: FORM_ROW_KEY,
    weightInGrams:
      weight !== undefined && unit ? convertWeight(weight, unit, WeightUnitChoices.g) : undefined,
    displayUnit: unit ?? WeightUnitChoices.lbs,
  };
};

export default function PrenatalFlowsheetTable(props: PrenatalFlowsheetTableProps) {
  const { gestationalAge, eddDate, observations, formValues, isLoading } = props;
  return (
    <Table.ScrollContainer
      type="native"
      minWidth={475}
      data-testid="prenatal-flowsheet-table"
      className={classes.tableScrollContainer}
    >
      <Table striped="even" className={classes.table}>
        <PrenatalFlowsheetThead />
        <PrenatalFlowsheetTbody
          eddDate={eddDate}
          isLoading={isLoading}
          formValues={formValues}
          observations={observations}
          gestationalAge={gestationalAge}
        />
      </Table>
    </Table.ScrollContainer>
  );
}

const PrenatalFlowsheetThead = () => {
  return (
    <Table.Thead>
      <Table.Tr className={classes.trHeader}>
        <Table.Th>
          <Stack gap={0}>
            <span>Wks.</span>
            <span>Days</span>
          </Stack>
        </Table.Th>
        <Table.Th>Date</Table.Th>
        <Table.Th>Weight</Table.Th>
        <Table.Th>BP</Table.Th>
        <Table.Th>Pulse</Table.Th>
        <Table.Th>Fundal Height</Table.Th>
        <Table.Th>FHT</Table.Th>
        <Table.Th>Fetal Position</Table.Th>
        <Table.Th>FM</Table.Th>
        <Table.Th>Edema</Table.Th>
        <Table.Th>Protein Glucose</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );
};

const PrenatalFlowsheetTbody = (props: PrenatalFlowsheetTableProps) => {
  const { gestationalAge, eddDate, observations, formValues, isLoading } = props;

  if (isLoading) {
    return (
      <Table.Tbody>
        <Table.Tr role="row" key="empty">
          <Table.Td role="cell" colSpan={10}>
            Loading...
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    );
  }

  // remove date as form always fill this field by default, so we don't consider it as a filled form
  const cleanedFormValues = omit(formValues, ["date"]);

  if (isEmpty(observations) && isEmpty(cleanedFormValues)) {
    return (
      <Table.Tbody>
        <Table.Tr role="row" key="empty">
          <Table.Td role="cell" colSpan={10}>
            No data available
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    );
  }

  const weightChanges = calculateWeightChanges([
    toFormWeightEntry(formValues),
    ...(observations ?? []).map(([flowsheetObservation, members]) => {
      const weightObservation = findObservationByCode(members, LOINCCodes.WeightMeasured);
      const unit = weightObservation?.valueQuantity?.unit;

      return {
        rowKey: flowsheetObservation.id,
        weightInGrams: weightObservation ? getWeightInGrams(weightObservation) : undefined,
        displayUnit: isWeightUnit(unit) ? unit : WeightUnitChoices.lbs,
      };
    }),
  ]);

  return (
    <Table.Tbody>
      <PrenatalFlowsheetFormRows formValues={formValues!} gestationalAge={gestationalAge} />
      <PrenatalFlowsheetObservationsRows
        eddDate={eddDate}
        observations={observations!}
        weightChanges={weightChanges}
      />
    </Table.Tbody>
  );
};
